import { useState, useEffect, useCallback, useRef } from 'react';
import { api, LitLegionTaskWithStatus, LitLegionTaskSubmission } from '@/lib/api';

interface UpdateStatusBody {
  submission?: LitLegionTaskSubmission;
  userInput?: string;
}

interface UseLitLegionTaskReturn {
  task: LitLegionTaskWithStatus | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  updateStatus: (status: string, body?: UpdateStatusBody) => Promise<void>;
}

export function useLitLegionTask(taskId: string | undefined): UseLitLegionTaskReturn {
  const [task, setTask] = useState<LitLegionTaskWithStatus | null>(null);
  const [loading, setLoading] = useState(!!taskId);
  const [error, setError] = useState<string | null>(null);
  const refreshRef = useRef(0);

  const fetchTask = useCallback(async () => {
    if (!taskId) {
      setLoading(false);
      return;
    }
    const id = ++refreshRef.current;
    try {
      setLoading(true);
      setError(null);
      const result = await api.getLitLegionTask(taskId);
      if (refreshRef.current === id) {
        setTask(result.task);
      }
    } catch (err) {
      if (refreshRef.current === id) {
        setError(err instanceof Error ? err.message : 'Failed to load task');
      }
    } finally {
      if (refreshRef.current === id) {
        setLoading(false);
      }
    }
  }, [taskId]);

  const updateStatus = useCallback(
    async (status: string, body?: UpdateStatusBody) => {
      if (!taskId) return;
      await api.updateLitLegionTaskStatus(taskId, status, body);
      await fetchTask();
    },
    [taskId, fetchTask],
  );

  useEffect(() => {
    fetchTask();
    return () => {
      refreshRef.current++;
    };
  }, [fetchTask]);

  return {
    task,
    loading,
    error,
    refresh: fetchTask,
    updateStatus,
  };
}
